import { useState } from "react";
import { toast } from "sonner";
import { Layers } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

export default function CreateSpacePage() {
  const { t, setAuth, user, lang } = useAuth();
  const [kind, setKind] = useState("band");
  const [name, setName] = useState("");
  const [style, setStyle] = useState("bagad");
  const [customStyle, setCustomStyle] = useState("");
  const [language, setLanguage] = useState("fr");
  const [prenom, setPrenom] = useState(user?.prenom || "");
  const [nom, setNom] = useState(user?.nom || "");
  const [instrument, setInstrument] = useState(user?.instrument || "");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register-group", {
        group_name: kind === "personal" ? "Espace personnel" : name, diminutif: "",
        style: kind === "school" ? "autre" : style,
        custom_style: kind === "school" ? "Enseignement" : customStyle,
        language, prenom, nom, password: "", instrument,
        kind,
        disciplines: ["pipeband", "bagad"].includes(style) && kind === "band" ? [style] : [],
      });
      toast.success(t("space.created"));
      setAuth(data);
      window.location.assign("/tableau-de-bord");
    } catch (err) {
      setError(err.friendly);
      setLoading(false);
    }
  };

  return (
    <div className="flex-1 flex items-start justify-center py-6" data-testid="create-space-page">
      <form onSubmit={submit} className="app-card p-6 space-y-4 w-full max-w-md anim-fade-up" data-testid="create-space-form">
        <h2 className="font-heading font-bold text-lg flex items-center gap-2"><Layers className="h-5 w-5 brand" /> {t(kind === "personal" ? "group_creation_personal" : kind === "school" ? "group_creation_school" : "group_creation")}</h2>
        <p className="text-sm t-secondary">{t("space.create_sub")}</p>
        {error && <p className="text-sm text-rose-400" data-testid="create-space-error">{error}</p>}
        <div>
          <Label className="t-secondary text-xs uppercase tracking-wider">{t("login.space_type_label")}</Label>
          <div className="grid grid-cols-3 gap-2 mt-1" data-testid="create-space-type">
            {[["band", "login.space_band", "login.space_band_desc", false], ["school", "login.space_school", "login.space_school_desc", !user?.is_superadmin], ["personal", "login.space_personal", "login.space_personal_desc", false]].map(([k, l, d, locked]) => (
              <button type="button" key={k} disabled={locked} onClick={() => !locked && setKind(k)}
                className={`min-w-0 px-2 py-2.5 rounded-lg border text-left transition-colors ${locked ? "border-app t-secondary opacity-60 cursor-not-allowed" : kind === k ? "brand-bg text-white border-transparent" : "border-app t-secondary hover:bg-white/5"}`}
                data-testid={`create-space-type-${k}`}>
                <span lang={lang} className="block text-[13px] font-semibold leading-tight break-words [hyphens:auto]">{t(l)}</span>
                <span className={`block text-[10px] leading-snug break-words mt-0.5 ${kind === k ? "text-white/80" : "t-secondary"}`}>{t(d)}</span>
                {locked && <span className="inline-block mt-1 text-[9px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400" data-testid="create-space-type-dev-badge">{t("login.space_dev")}</span>}
              </button>
            ))}
          </div>
        </div>
        {kind !== "personal" && (
        <div>
          <Label className="t-secondary text-xs uppercase tracking-wider">{t("group_name")}</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} required className="mt-1 bg-transparent border-app" data-testid="create-space-name-input" />
        </div>
        )}
        {kind === "band" && (
          <div>
            <Label className="t-secondary text-xs uppercase tracking-wider">{t("style_label")}</Label>
            <Select value={style} onValueChange={setStyle}>
              <SelectTrigger className="mt-1 bg-transparent border-app" data-testid="create-space-style-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="pipeband">Pipe Band</SelectItem>
                <SelectItem value="bagad">Bagad</SelectItem>
                <SelectItem value="autre">{t("cat.autre")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
        {kind === "band" && style === "autre" && (
          <div>
            <Label className="t-secondary text-xs uppercase tracking-wider">{t("custom_style")}</Label>
            <Input value={customStyle} onChange={(e) => setCustomStyle(e.target.value)} placeholder={t("login.custom_style_placeholder")} className="mt-1 bg-transparent border-app" data-testid="create-space-custom-style-input" />
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="t-secondary text-xs uppercase tracking-wider">{t("firstname")}</Label>
            <Input value={prenom} onChange={(e) => setPrenom(e.target.value)} required className="mt-1 bg-transparent border-app" data-testid="create-space-prenom-input" />
          </div>
          <div>
            <Label className="t-secondary text-xs uppercase tracking-wider">{t("lastname")}</Label>
            <Input value={nom} onChange={(e) => setNom(e.target.value)} className="mt-1 bg-transparent border-app" data-testid="create-space-nom-input" />
          </div>
        </div>
        {kind === "band" && (
          <div>
            <Label className="t-secondary text-xs uppercase tracking-wider">{t("instrument_label")}</Label>
            <Input value={instrument} onChange={(e) => setInstrument(e.target.value)} placeholder="ex. Cornemuse" className="mt-1 bg-transparent border-app" data-testid="create-space-instrument-input" />
          </div>
        )}
        <div>
          <Label className="t-secondary text-xs uppercase tracking-wider">{t("param.language_label")}</Label>
          <div className="flex gap-2 mt-1" data-testid="create-space-language">
            {[["fr", "Français"], ["en", "English"]].map(([k, l]) => (
              <button type="button" key={k} onClick={() => setLanguage(k)}
                className={`flex-1 px-3 py-2 rounded-lg border text-sm transition-colors ${language === k ? "brand-bg text-white border-transparent" : "border-app t-secondary hover:bg-white/5"}`}
                data-testid={`create-space-lang-${k}`}>{l}</button>
            ))}
          </div>
        </div>
        <Button type="submit" disabled={loading || (kind !== "personal" && !name.trim()) || !prenom.trim()} className="w-full brand-bg text-white" data-testid="create-space-submit">
          {loading ? t("creating") : t("create_group")}
        </Button>
      </form>
    </div>
  );
}
